import { useCallback, useEffect, useRef, useState } from 'react';

import FaveHeart from '@/components/app/contentPlayer/FaveHeart';
import TransportIcon, { TransportIconSpacer } from '@/components/app/contentPlayer/TransportIcon';
import { useAnalytics } from '@/hooks/analytics';
import { useTrackSession } from '@/hooks/app/useTrackSession';
import { useRouterPush } from '@/hooks/utils/useRouterPush';
import { formatTime } from '@/utils/app/formatTime';
import { calmLogger } from '@/utils/calmLogger';
import pauseIcon from 'icons/pause-filled.svg';
import playIcon from 'icons/play-filled.svg';
import stopIcon from 'icons/stop-filled.svg';

import { GuideLayoutProps } from '../GuideLayout/types';
import { CountLabel, TimerTransport, TransportContainer, TransportIcons, Wrapper } from './styles';

const COUNT_UP_PROGRAM_ID = 'bGLbqm4';
const MEDITATION_BELL_URL = 'https://assets.calm.com/b7f65067440bf81dbc67d4f9ec4a9c9c.m4a';

const Timer = ({ program, guide }: GuideLayoutProps) => {
	const { logEvent } = useAnalytics();
	const trackSession = useTrackSession(guide, program);
	const routerPush = useRouterPush();

	const intervalRef = useRef<ReturnType<typeof setInterval>>();
	const bellAudioRef = useRef<HTMLAudioElement>();

	const isCountingUp = program?.id === COUNT_UP_PROGRAM_ID;

	const [time, setTime] = useState(isCountingUp ? 0 : (guide?.timer_duration as number));
	const [isPlaying, setIsPlaying] = useState(true);

	const playBell = async () => {
		if (bellAudioRef.current) {
			try {
				await bellAudioRef.current.play();
			} catch (err) {
				// swallow error
			}
		}
	};

	const tick = useCallback(() => {
		setTime(storedTime => (isCountingUp ? storedTime + 1 : storedTime - 1));
	}, [isCountingUp]);

	const startTimer = useCallback(() => {
		intervalRef.current = setInterval(tick, 1000);
	}, [tick]);

	const onPause = () => {
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
			setIsPlaying(false);
		}
	};

	const onPlay = () => {
		if (time === 0) {
			setTime(isCountingUp ? 0 : (guide?.timer_duration as number));
		}
		startTimer();
		setIsPlaying(true);
	};

	const endSession = useCallback(
		async (endTime: number, skipTracking = false) => {
			onPause();

			if (!skipTracking) {
				await trackSession({
					wasCompleted: true,
					duration: isCountingUp ? endTime : (guide?.timer_duration as number) - endTime,
				});
			}

			await routerPush(`/app/program/${program?.id}`);
		},
		[guide?.timer_duration, isCountingUp, program?.id, routerPush, trackSession],
	);

	const onStop = async () => {
		await endSession(time, true);
	};

	useEffect(() => {
		(async () => {
			if (isCountingUp && time % (guide?.timer_duration as number) === 0) {
				await playBell();
			} else if (time <= 0) {
				await Promise.all([playBell(), endSession(time)]);
			}
		})().catch(error => calmLogger.error('Error in guide Timer playBell', {}, error));
	}, [endSession, guide?.timer_duration, isCountingUp, time]);

	useEffect(() => {
		logEvent({
			eventName: 'Session : Began',
			eventProps: {
				guide_id: guide?.id as string,
				session_type: program?.meditation_type as string,
				guide_id_free: true,
				guide_title: guide?.short_title || (guide?.title as string),
				program_id: program?.id as string,
				program_title: program?.title as string,
			},
		});
	}, [
		guide?.id,
		guide?.short_title,
		guide?.title,
		logEvent,
		program?.id,
		program?.meditation_type,
		program?.title,
	]);

	useEffect(() => {
		startTimer();
	}, [startTimer]);

	useEffect(() => {
		return () => {
			if (!isPlaying) {
				endSession(time).catch(error => calmLogger.error('Error in guide Timer endSession', {}, error));
			}
		};
	}, [endSession, isPlaying, time]);

	useEffect(() => {
		bellAudioRef.current = new Audio(MEDITATION_BELL_URL);
	}, []);

	if (!guide) {
		return null;
	}

	return (
		<Wrapper>
			<CountLabel>{formatTime(time)}</CountLabel>
			<TimerTransport>
				<TransportContainer>
					<FaveHeart withBorder guideId={guide.id} guideTitle={guide.title} />
					<TransportIcons>
						<TransportIconSpacer $width="33.33%" />
						<TransportIcon
							isPrimary
							alignment="center"
							aria-label={isPlaying ? 'pause' : 'play'}
							icon={isPlaying ? pauseIcon : playIcon}
							onClick={isPlaying ? onPause : onPlay}
							sizeTheme="large"
							width="33.33%"
						/>
						<TransportIcon
							alignment="center"
							aria-label="stop playing"
							icon={stopIcon}
							onClick={() => onStop()}
							sizeTheme="large"
							width="33.33%"
						/>
					</TransportIcons>
				</TransportContainer>
			</TimerTransport>
		</Wrapper>
	);
};

export default Timer;
